import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import { formatDateLocale } from '../utils/formatters';
import useChartZoomWindow from './useChartZoomWindow';
import AnalyzeZoomButton from './AnalyzeZoomButton';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

// Seuils normaux
const TEMP_THRESHOLDS = { min: 5, max: 25 };
const HUMIDITY_THRESHOLDS = { min: 40, max: 70 };

const EnvChart = ({ readings, darkMode = false, onQuickAnnotate, onAnalyzeZoom }) => {
  const { t } = useTranslation();
  const zoom = useChartZoomWindow({ readings, onAnalyzeZoom, panModifierKey: 'shift', wheelSpeed: 0.1, limitToData: true });
  const chartRef = zoom.chartRef;
  const [quickAnnot, setQuickAnnot] = useState(null);

  const handleChartClick = useCallback((event) => {
    if (!onQuickAnnotate || !chartRef.current) return;
    const chart = chartRef.current;
    const rect  = chart.canvas.getBoundingClientRect();
    const xPos  = event.clientX - rect.left;
    const yPos  = event.clientY - rect.top;
    const elements = chart.getElementsAtEventForMode(event.nativeEvent || event,
      'nearest', { intersect: false }, false);
    if (!elements.length) { setQuickAnnot(null); return; }
    const idx = elements[0].index;
    const reading = readings[idx];
    if (!reading) return;
    setQuickAnnot({ x: xPos, y: Math.max(40, yPos - 60), timestamp: reading.timestamp });
  }, [onQuickAnnotate, readings, chartRef]);
  
  if (!readings || readings.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: darkMode ? '#E8EDF2' : '#4A5568'
      }}>
        {t('charts.empty.env')}
      </div>
    );
  }

  const labels = readings.map(r =>
    // Label d'axe X (affichage) : heure « HH:MM » localisée (FR/EN identiques, colon).
    formatDateLocale(r.timestamp, { hour: '2-digit', minute: '2-digit' })
  );

  const data = {
    labels,
    datasets: [
      {
        label: t('charts.axis.temperature'),
        data: readings.map(r => r.temperature),
        borderColor: darkMode ? 'rgba(255, 99, 132, 0.9)' : 'rgba(216, 90, 48, 1)',
        backgroundColor: darkMode ? 'rgba(255, 99, 132, 0.15)' : 'rgba(216, 90, 48, 0.15)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
        tension: 0.3,
        yAxisID: 'y'
      },
      {
        label: t('charts.axis.humidity'),
        data: readings.map(r => r.humidity),
        borderColor: darkMode ? '#90CAF9' : 'rgba(77, 166, 255, 1)',
        backgroundColor: darkMode ? 'rgba(144,202,249,0.1)' : 'rgba(77, 166, 255, 0.15)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
        tension: 0.3,
        yAxisID: 'y1'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: darkMode ? '#E1E1E1' : '#2C3E50',
          font: { size: 12, weight: '600' },
          usePointStyle: true,
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: darkMode ? '#E1E1E1' : '#2C3E50',
        bodyColor: darkMode ? '#E8EDF2' : '#4A5568',
        borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(77, 166, 255, 0.5)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const unit = context.dataset.yAxisID === 'y' ? '°C' : '%';
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} ${unit}`;
          }
        }
      },
      zoom: zoom.zoomOptions
    },
    scales: {
      x: {
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          drawBorder: false
        },
        ticks: {
          color: darkMode ? '#E8EDF2' : '#4A5568',
          font: { size: 11 },
          maxTicksLimit: 20
        }
      },
      y: {
        type: 'linear',
        position: 'left',
        min: Math.min(...readings.map(r => r.temperature)) - 2,
        max: Math.max(...readings.map(r => r.temperature)) + 2,
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          drawBorder: false
        },
        ticks: {
          color: darkMode ? 'rgba(255, 99, 132, 0.9)' : 'rgba(216, 90, 48, 1)',
          font: { size: 11, weight: '600' }
        },
        title: {
          display: true,
          text: t('charts.axis.temperature'),
          color: darkMode ? 'rgba(255, 99, 132, 0.9)' : 'rgba(216, 90, 48, 1)',
          font: { size: 12, weight: '600' }
        }
      },
      y1: {
        type: 'linear',
        position: 'right',
        min: Math.min(...readings.map(r => r.humidity)) - 5,
        max: Math.max(...readings.map(r => r.humidity)) + 5,
        grid: {
          drawOnChartArea: false
        },
        ticks: {
          color: darkMode ? '#90CAF9' : 'rgba(77, 166, 255, 1)',
          font: { size: 11, weight: '600' }
        },
        title: {
          display: true,
          text: t('charts.axis.humidity'),
          color: darkMode ? '#90CAF9' : 'rgba(77, 166, 255, 1)',
          font: { size: 12, weight: '600' }
        }
      }
    }
  };

  // Calculer les alertes
  const tempAlerts = readings.filter(r => r.temperature < TEMP_THRESHOLDS.min || r.temperature > TEMP_THRESHOLDS.max);
  const humidityAlerts = readings.filter(r => r.humidity < HUMIDITY_THRESHOLDS.min || r.humidity > HUMIDITY_THRESHOLDS.max);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
        <AnalyzeZoomButton zoom={zoom} />
        <button
          onClick={zoom.resetZoom}
          style={{
            padding: '6px 12px',
            background: '#1B77F1',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {t('common.zoom.reset')}
        </button>
      </div>
      <div style={{ position: 'relative', height: '300px' }} onClick={handleChartClick}>
        <Line ref={chartRef} data={data} options={options} />
        {quickAnnot && onQuickAnnotate && (
          <div style={{
            position: 'absolute', left: quickAnnot.x, top: quickAnnot.y,
            transform: 'translateX(-50%)',
            background: darkMode ? '#1E1E32' : '#fff',
            border: '1.5px solid #1B77F1', borderRadius: '8px', padding: '8px 12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10,
            whiteSpace: 'nowrap', pointerEvents: 'all'
          }}>
            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>
              {formatDateLocale(quickAnnot.timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={(e) => { e.stopPropagation(); onQuickAnnotate(quickAnnot.timestamp); setQuickAnnot(null); }}
                style={{ padding: '4px 10px', borderRadius: '6px', background: '#1B77F1', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                {t('session.annotations.quickAdd')}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setQuickAnnot(null); }}
                aria-label={t('common.close')}
                style={{ padding: '4px 8px', borderRadius: '6px', background: 'none',
                  border: '1px solid #e0e0e0', cursor: 'pointer', fontSize: '12px', color: '#6B7280' }}>
                ×
              </button>
            </div>
          </div>
        )}
      </div>
      
      {(tempAlerts.length > 0 || humidityAlerts.length > 0) && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: darkMode ? 'rgba(239, 159, 39, 0.1)' : 'rgba(239, 159, 39, 0.1)',
          borderLeft: darkMode ? '3px solid rgba(239, 159, 39, 0.8)' : '3px solid rgba(239, 159, 39, 1)',
          borderRadius: '4px',
          fontSize: '13px',
          color: darkMode ? '#E1E1E1' : '#2C3E50'
        }}>
          <strong>{t('charts.env.alertsTitle')}</strong>
          {tempAlerts.length > 0 && (
            <div style={{ marginTop: '6px' }}>
              • {t('charts.env.tempAlert', { count: tempAlerts.length })}
            </div>
          )}
          {humidityAlerts.length > 0 && (
            <div style={{ marginTop: '6px' }}>
              • {t('charts.env.humidityAlert', { count: humidityAlerts.length })}
            </div>
          )}
        </div>
      )}
      
      {/* Instructions zoom - Responsive */}
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(27, 119, 241, 0.05)',
        borderRadius: '6px',
        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(27, 119, 241, 0.15)',
        fontSize: '13px',
        color: darkMode ? '#E8EDF2' : '#4A5568',
        textAlign: 'center',
        lineHeight: '1.5'
      }}>
        <span style={{ display: 'none' }} className="mobile-only">
          💡 {t('common.zoom.touch')}
        </span>
        <span className="desktop-only">
          💡 {t('common.zoom.desktop')}
        </span>
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: darkMode ? '#B8BFC7' : '#6B7280', margin: '8px 0 0', fontStyle: 'italic', lineHeight: '1.5' }}>
        {t('charts.env.footnote')}
      </p>
    </div>
  );
};

export default React.memo(EnvChart, (prev, next) =>
  prev.readings === next.readings &&
  prev.darkMode === next.darkMode &&
  prev.onAnalyzeZoom === next.onAnalyzeZoom
);
